import { Button, Grid, MenuItem, Select, useTheme } from "@mui/material";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { ChannelAutoCompleteDataModel } from "components/MprcAutoComplete/AutoCompleteDataModel";
import { AutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical";
import { CoreFilterCommand } from "generated/CoreApi_v1";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TopicAutoCompleteHierarchical } from "views/foundation/topic/components/TopicAutoCompleteHierarchical";
import { UserAutoComplete } from "views/foundation/user/components/UserAutoComplete";
import { IAdvFilterPickerProps, IAdvFilterResult } from "views/general/AdvancedFilter/interfaces";
import { ChannelAutoComplete } from "views/knowledge/channels/components/ChannelAutoComplete";
import { PublicationAutoComplete } from "views/knowledge/publication/components/PublicationAutoComplete";

////////////////////////////////////////////////////////////////////////////////

type MentioningCommandNames =
    | CoreFilterCommand.KnowledgeMentioningUser
    | CoreFilterCommand.KnowledgeMentioningTopic
    | CoreFilterCommand.KnowledgeMentioningChannel
    | CoreFilterCommand.KnowledgeMentioningPublication;

////////////////////////////////////////////////////////////////////////////////

export const MentionTypePicker: React.FC<IAdvFilterPickerProps> = (props) => {

    const { filter, onApply, onError, onCancel } = props;

    const { t } = useTranslation("knowledge");
    const theme = useTheme();

    const [mentionType, setMentionType] = useState<MentioningCommandNames>(CoreFilterCommand.KnowledgeMentioningUser);
    const [selectedItem, setSelectedItem] = useState<AutoCompleteHierarchicalModel | AutoCompleteDataModel>();

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const handleSelectItem = (items: (AutoCompleteHierarchicalModel | AutoCompleteDataModel)[]) => {
        if (items.length > 0) {
            setSelectedItem(items[0]);
        }
        else {
            setSelectedItem(undefined);
        }
    };

    ///
    const handleChangeType = (type: MentioningCommandNames) => {
        setMentionType(type);
        setSelectedItem(undefined);
    };

    ///
    const handleApply = () => {
        if (selectedItem == undefined || filter.options == undefined) return;

        const selectedFilter = filter.options.find(f => f.command == mentionType);
        const result = {
            command: selectedFilter!.command,
            text: `${selectedFilter!.displayName.replace("...", "")}: ${selectedItem.label}`,
            value: selectedItem.key,
        } as IAdvFilterResult;

        onApply(result, filter);
    };

    ///
    const getLabelName = (command: CoreFilterCommand): string | undefined => {
        switch (command) {
            case CoreFilterCommand.KnowledgeMentioningUser:
                return t("shared:SysObjects.User");
            case CoreFilterCommand.KnowledgeMentioningTopic:
                return t("shared:SysObjects.Topic");
            case CoreFilterCommand.KnowledgeMentioningChannel:
                return t("shared:SysObjects.Channel");
            case CoreFilterCommand.KnowledgeMentioningPublication:
                return t("shared:SysObjects.Publication");
            default:
                break;
        }
    };
    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return <>
        <Grid container spacing={2}>
            {filter.options != undefined &&
                <Grid size={{ xs: 2 }}>
                    <Select
                        value={mentionType}
                        fullWidth
                        color="warning"
                        onChange={e => handleChangeType(e.target.value as MentioningCommandNames)}
                        sx={{
                            svg: { color: theme.palette["warning"].main },
                            '& .MuiInputBase-input': {
                                color: theme.palette["warning"].main,
                            },
                            '&:before': {
                                borderColor: theme.palette["warning"].main,
                            }
                        }}
                    >
                        {filter.options.map(item =>
                            <MenuItem key={item.command} value={item.command}>{getLabelName(item.command)}</MenuItem>
                        )}
                    </Select>
                </Grid>
            }
            <Grid size="grow">
                {
                    mentionType == CoreFilterCommand.KnowledgeMentioningUser &&
                    <UserAutoComplete
                        multiSelect={false}
                        placeholder="Selecione um usuário"
                        color="warning"
                        selectedItems={(selectedItem != undefined ? [selectedItem] : []) as AutoCompleteDataModel[]}
                        onChange={(items) => handleSelectItem(items)}
                        onError={onError}
                        sx={{
                            '& .MuiInput-underline:before': { borderBottomColor: theme.palette["warning"].main },
                            input: {
                                '&::placeholder': {
                                    color: theme.palette["warning"].main,
                                    opacity: 1,
                                },
                            }
                        }}
                    />
                }
                {
                    mentionType == CoreFilterCommand.KnowledgeMentioningTopic &&
                    <TopicAutoCompleteHierarchical
                        selectedMode="free"
                        multiSelect={false}
                        placeholder={t("AdvFilter.Picker.Topic.Placeholder", { ns: "shared" })}
                        selectedItems={(selectedItem != undefined ? [selectedItem] : []) as AutoCompleteHierarchicalModel[]}
                        onChange={(items) => handleSelectItem(items)}
                        color="warning"
                        onError={onError}
                        sx={{
                            '& .MuiInput-underline:before': { borderBottomColor: theme.palette["warning"].main },
                            input: {
                                '&::placeholder': {
                                    color: theme.palette["warning"].main,
                                    opacity: 1,
                                },
                            }
                        }}
                    />
                }
                {
                    mentionType == CoreFilterCommand.KnowledgeMentioningChannel &&
                    <ChannelAutoComplete
                        multiSelect={false}
                        placeholder={t("AdvFilter.Picker.Channel.Placeholder", { ns: "shared" })}
                        color="warning"
                        selectedItems={selectedItem != undefined ? [selectedItem as ChannelAutoCompleteDataModel] : []}
                        onChange={(items) => handleSelectItem(items)}
                        onError={onError}
                        sx={{
                            '& .MuiInput-underline:before': { borderBottomColor: theme.palette["warning"].main },
                            input: {
                                '&::placeholder': {
                                    color: theme.palette["warning"].main,
                                    opacity: 1,
                                },
                            }
                        }}
                    />
                }
                {
                    mentionType == CoreFilterCommand.KnowledgeMentioningPublication &&
                    <PublicationAutoComplete
                        multiSelect={false}
                        placeholder={t("Feed.AdvancedFilter.PublicationPlaceholder")}
                        color="warning"
                        selectedItems={(selectedItem != undefined ? [selectedItem] : []) as AutoCompleteDataModel[]}
                        onChange={(items) => handleSelectItem(items)}
                        onError={onError}
                        sx={{
                            '& .MuiInput-underline:before': { borderBottomColor: theme.palette["warning"].main },
                            input: {
                                '&::placeholder': {
                                    color: theme.palette["warning"].main,
                                    opacity: 1,
                                },
                            }
                        }}
                    />
                }
            </Grid>
            <Grid>
                <Button
                    variant="text"
                    title={t("Feed.AdvancedFilter.ApplyButtonTitle")}
                    color="highlight"
                    disabled={selectedItem == undefined}
                    onClick={handleApply}
                >
                    {t("Feed.AdvancedFilter.ApplyButtonText")}
                </Button>
                <Button
                    variant="text"
                    color="highlight"
                    title={t("Feed.AdvancedFilter.CancelButtonTitle")}
                    onClick={onCancel}
                >
                    {t("Feed.AdvancedFilter.CancelButtonText")}
                </Button>
            </Grid>
        </Grid>
    </>;
};
